// ---------------------------------------------------------------------------
// SWEEP S17. RECORDING A TRONC, FROM THE SCREEN.
//
// V23c built both writes (`app.record_tronc_scheme`, `app.record_tronc_distribution`)
// and the pay screen drew what they wrote, but nothing in the product could call
// either, so a hospitality agency could read a tronc and never record one.
//
// SEPARATE FROM Pay.tsx ON PURPOSE. A share is typed in major units and sent in
// minor ones, and that conversion is arithmetic; `verify-V23c.sh` refuses any on
// the pay screen. The conversion is `minorFrom`, the rate editor's, so the
// exponent question has one answer in the product.
//
// The independence claim has no default (the route refuses a missing one), and the
// standings come from the server with their labels.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";

import { send, type RequestSpec } from "../api/auth";
import { tronc as troncApi, type TroncScheme } from "../api/tronc";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { minorFrom } from "../system/payRules";
import type { PayRow } from "../system/pay";

type State = { id: string; label: string; description: string };

export function RecordTronc({ shifts, onRecorded }: { shifts: PayRow[]; onRecorded: () => void }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [schemes, setSchemes] = useState<TroncScheme[] | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [tick, setTick] = useState({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void send<{ schemes?: TroncScheme[] }>(troncApi.schemes(apiBase)).then(({ status, body }) =>
      setSchemes(status === 200 && body.schemes ? body.schemes : []),
    );
  }, [apiBase, tick]);
  useEffect(() => {
    void send<{ tronc_states?: State[] }>(troncApi.states(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.tronc_states) setStates(body.tronc_states);
    });
  }, [apiBase]);

  async function submit(call: RequestSpec, ok: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    setDone(null);
    const res = await send<{ error?: { message: string } }>(call);
    setBusy(false);
    if (res.status === 200 || res.status === 201) {
      setDone(ok);
      return true;
    }
    setError(refusal(res.body?.error) ?? t("That could not be recorded."));
    return false;
  }

  if (schemes === null) return <p className="muted">{t("Reading the tronc arrangements…")}</p>;
  const open = schemes.filter((s) => s.closed_on === null);

  return (
    <div className="stack">
      {error ? <p role="alert">{error}</p> : null}
      {done ? <p role="status">{done}</p> : null}
      {open.length > 0 && shifts.length > 0 ? (
        <ShareForm
          shifts={shifts}
          schemes={open}
          states={states}
          busy={busy}
          onSubmit={async (timesheetId, body) => {
            if (await submit(troncApi.record(apiBase, timesheetId, body), t("The share is recorded."))) onRecorded();
          }}
        />
      ) : shifts.length === 0 ? (
        <p className="muted">{t("A share is recorded against a shift with hours, and this event has none yet.")}</p>
      ) : null}
      <SchemeForm
        busy={busy}
        first={open.length === 0}
        onSubmit={async (body) => {
          if (await submit(troncApi.recordScheme(apiBase, body), t("The arrangement is recorded."))) setTick({});
        }}
      />
    </div>
  );
}

function ShareForm({
  shifts,
  schemes,
  states,
  busy,
  onSubmit,
}: {
  shifts: PayRow[];
  schemes: TroncScheme[];
  states: State[];
  busy: boolean;
  onSubmit: (timesheetId: string, body: Record<string, unknown>) => Promise<void>;
}) {
  const { locale, t } = useT();
  const [timesheetId, setTimesheetId] = useState("");
  const [schemeId, setSchemeId] = useState(schemes[0]?.id ?? "");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [amount, setAmount] = useState("");
  const [state, setState] = useState("");
  const [local, setLocal] = useState<string | null>(null);
  const scheme = schemes.find((s) => s.id === schemeId);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const minor = scheme ? minorFrom(amount, scheme.currency) : null;
        if (minor === null || minor <= 0) {
          setLocal(t("Type the share as an amount above zero, for example 12.50."));
          return;
        }
        setLocal(null);
        void onSubmit(timesheetId, {
          scheme_id: schemeId,
          period_start: periodStart,
          period_end: periodEnd,
          amount_minor: minor,
          state,
        });
      }}
    >
      <h3>{t("Record a share")}</h3>
      <div className="field">
        <label htmlFor="tronc-shift">{t("Shift")}</label>
        <select id="tronc-shift" required value={timesheetId} onChange={(e) => setTimesheetId(e.target.value)}>
          <option value="">{t("Choose a shift…")}</option>
          {shifts.map((s) => (
            <option key={s.timesheet_id} value={s.timesheet_id}>
              {s.worker_name} — {translateLabel(locale, s.role_name)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="tronc-scheme">{t("Arrangement")}</label>
        <select id="tronc-scheme" required value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
          {schemes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.reference} ({s.currency})
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="tronc-from">{t("Period from")}</label>
          <input id="tronc-from" type="date" required value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="tronc-to">{t("Period to")}</label>
          <input id="tronc-to" type="date" required min={periodStart} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="tronc-amount">{scheme ? t("Share ({currency})", { currency: scheme.currency }) : t("Share")}</label>
        <input id="tronc-amount" inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        <p className="field-hint">{t("As the troncmaster reported it. It is never added to pay.")}</p>
        {local ? <p role="alert">{local}</p> : null}
      </div>
      <div className="field">
        <label htmlFor="tronc-state">{t("Standing")}</label>
        <select id="tronc-state" required value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {translateLabel(locale, s.label)}
            </option>
          ))}
        </select>
      </div>
      <button className="btn" type="submit" disabled={busy}>
        {t("Record the share")}
      </button>
    </form>
  );
}

function SchemeForm({
  busy,
  first,
  onSubmit,
}: {
  busy: boolean;
  first: boolean;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useT();
  const [reference, setReference] = useState("");
  const [troncmaster, setTroncmaster] = useState("");
  const [currency, setCurrency] = useState("");
  const [openedOn, setOpenedOn] = useState("");
  const [independent, setIndependent] = useState<"" | "yes" | "no">("");

  return (
    <details open={first}>
      <summary>{first ? t("Set up a tronc arrangement first") : t("Add another tronc arrangement")}</summary>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit({
            reference,
            troncmaster_reference: troncmaster,
            currency: currency.trim().toUpperCase(),
            opened_on: openedOn,
            is_independent_of_employer: independent === "yes",
          });
        }}
      >
        <div className="field">
          <label htmlFor="scheme-ref">{t("Reference")}</label>
          <input id="scheme-ref" required maxLength={40} value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="scheme-master">{t("Troncmaster reference")}</label>
          <input id="scheme-master" required maxLength={40} value={troncmaster} onChange={(e) => setTroncmaster(e.target.value)} />
          <p className="field-hint">{t("The reference the tronc is run under, without spaces — not a person's name.")}</p>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="scheme-currency">{t("Currency")}</label>
            <input id="scheme-currency" required maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="scheme-opened">{t("Opened on")}</label>
            <input id="scheme-opened" type="date" required value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} />
          </div>
        </div>
        <fieldset className="field">
          <legend>{t("Is the troncmaster independent of the employer?")}</legend>
          <label>
            <input type="radio" name="scheme-independent" required checked={independent === "yes"} onChange={() => setIndependent("yes")} /> {t("Yes")}
          </label>
          <label>
            <input type="radio" name="scheme-independent" checked={independent === "no"} onChange={() => setIndependent("no")} /> {t("No")}
          </label>
          <p className="field-hint">{t("Recorded as this agency declares it. Nothing here checks it.")}</p>
        </fieldset>
        <button className="btn" type="submit" disabled={busy}>
          {t("Record the arrangement")}
        </button>
      </form>
    </details>
  );
}
